import { useId, useMemo } from "react";

import { lang } from "../../i18n/i18n";
import { lineLabel } from "../../map/lineNames";
import { missionFile } from "../../map/units/missionImages";
import { symbolSvg } from "../../map/units/symbols";

/** Croquis tactique de l'animation d'ouverture (demande d'Alexis du 26/10/2026) : sous l'accroche, un fuseau de
 *  brigade sur fond quadrillé ; les limites et deux LIMA se tracent, deux bataillons amis et deux ennemis
 *  apparaissent, les amis franchissent la première LIMA pendant que l'ennemi recule derrière la seconde, puis les
 *  missions se dessinent (ATTAQUER, FIXER ; FREINER pour l'ennemi) et le croquis reste immobile jusqu'au fondu de
 *  l'animation. Tout est joué entre 1,7 s et 5,6 s : la durée de l'ouverture (8 s) ne change pas.
 *
 *  Dessins de l'application, rien de redessiné : symboles APP-6 de milsymbol (`symbolSvg`, comme les croquis des
 *  modes d'action), images de mission du catalogue (`/mission-symbols`, celles de la carte), LIMA en trait plein
 *  bleu ami portant leur nom (`lineLabel` : L1, PL1 en anglais). Chorégraphie en CSS (`intro-sk-*`, index.css),
 *  état final statique d'emblée si le poste demande moins de mouvement. */

export const SKETCH_W = 440;
export const SKETCH_H = 160;
/** Borne de fin des animations du croquis (s ; dernière image à 5,6 s) : bien avant le fondu de l'ouverture (7,4 s). */
export const SKETCH_END_S = 5.8;

/** Bataillons (échelon II) : blindé et infanterie amis, infanterie mécanisée et blindé ennemis. */
const FRIEND_ARMOUR = "10031000161205000000";
const FRIEND_INFANTRY = "10031000161211000000";
const HOSTILE_INFANTRY = "10061000161211020000";
const HOSTILE_ARMOUR = "10061000161205000000";

const FRIEND = "var(--color-app6-friend)";
/** Limites du fuseau (haut, bas) et LIMA (abscisse au centre). */
const TOP = 22;
const BOTTOM = 138;
const L1_X = 316;
const L2_X = 190;

interface Placed {
  sidc: string;
  x: number;
  y: number;
}

/** Positions de départ ; le déplacement est porté par le groupe (classes `intro-sk-advance`, `intro-sk-delay`). */
const FRIENDS: Placed[] = [
  { sidc: FRIEND_ARMOUR, x: 398, y: 58 },
  { sidc: FRIEND_INFANTRY, x: 398, y: 104 },
];
const HOSTILES: Placed[] = [
  { sidc: HOSTILE_INFANTRY, x: 238, y: 58 },
  // Blindé ENI en appui de l'unité qui freine (demande d'Alexis du 26/10/2026) : en retrait, derrière elle.
  { sidc: HOSTILE_ARMOUR, x: 148, y: 110 },
];

/** Missions, à leur place finale. Images à l'échelle du croquis (les images @2x sont en points × 2). */
const MISSIONS = [
  { file: "Atk180To0", w: 210, h: 134, scale: 0.28, x: 248, y: 58, delay: "intro-sk-m1" },
  { file: "Fix180To0", w: 234, h: 158, scale: 0.27, x: 244, y: 104, delay: "intro-sk-m2" },
  // FREINER : l'unité ennemie qui freine (infanterie, position finale 170, 58 après son repli) au centre de la boucle
  // du symbole (point (705, 135) de l'image de 788 × 268, demande d'Alexis du 26/10/2026).
  { file: "EniFreiner", w: 788, h: 268, scale: 0.19, x: 170 - (705 - 394) * 0.19, y: 58 - (135 - 134) * 0.19, delay: "intro-sk-m3" },
  // APPUYER (ENI) : flèche du blindé en appui (position finale 110, 106) vers l'avant, empennage contre l'unité ;
  // image tournée d'un demi-tour comme sur la carte (variante « 0To180 », texte à l'endroit une fois tournée).
  // Flèche APP tournée vers l'unité appuyée (170, 58), arrêtée avant l'itinéraire de FREINER.
  { file: "EniApp0To180", w: 272, h: 79, scale: 0.2, x: 115, y: 90, delay: "intro-sk-m4", rotate: 150 },
] as const satisfies readonly { file: string; w: number; h: number; scale: number; x: number; y: number; delay: string; rotate?: number }[];

function lima(x: number): string {
  // Légère courbure, comme une LIMA posée sur le terrain.
  return `M ${x + 4} ${TOP - 8} C ${x - 8} ${TOP + 30}, ${x + 10} ${BOTTOM - 40}, ${x - 2} ${BOTTOM + 8}`;
}

export function IntroSketch({ className = "" }: { className?: string }) {
  const uid = useId().replace(/:/g, "");
  const icons = useMemo(() => {
    const out = new Map<string, { href: string; w: number; h: number }>();
    for (const sidc of [FRIEND_ARMOUR, FRIEND_INFANTRY, HOSTILE_INFANTRY, HOSTILE_ARMOUR]) {
      const svg = symbolSvg(sidc, 17);
      const w = Number(/width="([\d.]+)"/.exec(svg)?.[1] ?? 28);
      const h = Number(/height="([\d.]+)"/.exec(svg)?.[1] ?? 22);
      out.set(sidc, { href: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, w, h });
    }
    return out;
  }, []);
  const language = lang() === "en" ? "en" : "fr";
  const unit = (u: Placed, key: string) => {
    const icon = icons.get(u.sidc)!;
    return (
      <image key={key} href={icon.href} x={u.x - icon.w / 2} y={u.y - icon.h / 2} width={icon.w} height={icon.h} />
    );
  };
  const chip = (x: number, y: number, name: string, cls: string) => {
    const text = lineLabel(name, language);
    const w = text.length * 6 + 10;
    return (
      <g className={`intro-sk-fade ${cls}`}>
        <rect x={x - w / 2} y={y - 7.5} width={w} height={15} rx={4} fill="var(--color-surface)"
          stroke="var(--color-line-strong)" />
        <text x={x} y={y + 0.5} textAnchor="middle" dominantBaseline="middle" fontSize={10} fontWeight={600}
          fill="var(--color-friend)">
          {text}
        </text>
      </g>
    );
  };
  const boundary = (y: number, cls: string) => (
    <g>
      <g mask={`url(#${uid}-limits)`}>
        <path className={`intro-sk-draw ${cls}`} d={`M 16 ${y} L ${SKETCH_W - 16} ${y}`} pathLength={1}
          stroke={FRIEND} strokeWidth={1.6} fill="none" />
      </g>
      <text className="intro-sk-fade intro-sk-t3" x={(L1_X + L2_X) / 2} y={y + 0.5} textAnchor="middle"
        dominantBaseline="middle" fontSize={11} fontWeight={600} fill={FRIEND} stroke="var(--color-canvas)"
        strokeWidth={4} paintOrder="stroke">
        X
      </text>
    </g>
  );
  return (
    <svg
      className={`intro-sketch ${className}`}
      viewBox={`0 0 ${SKETCH_W} ${SKETCH_H}`}
      width={SKETCH_W}
      height={SKETCH_H}
      aria-hidden
      data-intro-sketch
    >
      <defs>
        <pattern id={`${uid}-grid`} width={20} height={20} patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="var(--color-line-strong)" strokeWidth={0.6} />
        </pattern>
        <radialGradient id={`${uid}-fade`} cx="50%" cy="50%" r="55%">
          <stop offset="55%" stopColor="white" stopOpacity={1} />
          <stop offset="100%" stopColor="white" stopOpacity={0} />
        </radialGradient>
        <linearGradient id={`${uid}-ends`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="white" stopOpacity={0} />
          <stop offset="0.14" stopColor="white" stopOpacity={1} />
          <stop offset="0.86" stopColor="white" stopOpacity={1} />
          <stop offset="1" stopColor="white" stopOpacity={0} />
        </linearGradient>
        {/* Extrémités des limites fondues dans le fond, comme le quadrillage. */}
        <mask id={`${uid}-limits`} maskUnits="userSpaceOnUse" x={0} y={0} width={SKETCH_W} height={SKETCH_H}>
          <rect width={SKETCH_W} height={SKETCH_H} fill={`url(#${uid}-ends)`} />
        </mask>
        <mask id={`${uid}-mask`}>
          <rect width={SKETCH_W} height={SKETCH_H} fill={`url(#${uid}-fade)`} />
        </mask>
      </defs>
      {/* Fond quadrillé, fondu sur les bords : aucun cadre. */}
      <rect className="intro-sk-fade intro-sk-t0" width={SKETCH_W} height={SKETCH_H} fill={`url(#${uid}-grid)`}
        mask={`url(#${uid}-mask)`} />
      {boundary(TOP, "intro-sk-t1")}
      {boundary(BOTTOM, "intro-sk-t1")}
      <path className="intro-sk-draw intro-sk-t2" d={lima(L1_X)} pathLength={1} stroke={FRIEND} strokeWidth={2}
        fill="none" />
      <path className="intro-sk-draw intro-sk-t2b" d={lima(L2_X)} pathLength={1} stroke={FRIEND} strokeWidth={2}
        fill="none" />
      {chip(L1_X + 4, TOP - 15, "L1", "intro-sk-t3")}
      {chip(L2_X + 4, TOP - 15, "L2", "intro-sk-t3b")}
      <g className="intro-sk-delay">
        <g className="intro-sk-pop intro-sk-u1">{HOSTILES.map((u, i) => unit(u, `e${i}`))}</g>
      </g>
      <g className="intro-sk-advance">
        <g className="intro-sk-pop intro-sk-u2">{FRIENDS.map((u, i) => unit(u, `a${i}`))}</g>
      </g>
      {MISSIONS.map((m) => {
        const w = m.w * m.scale;
        const h = m.h * m.scale;
        const rotate = "rotate" in m ? m.rotate : 0;
        return (
          <g key={m.file} transform={rotate ? `rotate(${rotate} ${m.x} ${m.y})` : undefined}>
            <image className={`intro-sk-wipe ${m.delay}`} href={missionFile(m.file)} x={m.x - w / 2} y={m.y - h / 2}
              width={w} height={h} />
          </g>
        );
      })}
    </svg>
  );
}
